import React from 'react';
import MatterDropdown from '../MatterDropdown';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import Brand from './Brand';
import Navigation from './Navigation';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useNavigation } from '@/components/contexts/NavigationContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DrawerProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Drawer = ({ sidebarOpen, setSidebarOpen }: DrawerProps) => {
  const { t } = useTranslation('common');
  const { collapsed, setCollapsed } = useNavigation();

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  return (
    <>
      {sidebarOpen && (
        <div data-navigation-drawer="true" className="relative z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-xs" />
          <div className="fixed inset-0 flex">
            <div className="relative mr-16 flex w-full max-w-xs flex-1">
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button
                  type="button"
                  className="-m-2.5 p-2.5"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">{t('close-sidebar')}</span>
                  <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
              <div
                className="flex grow flex-col gap-y-5 overflow-y-auto bg-[hsl(217,47%,20%)] px-6 pb-4"
                onClick={(event) => {
                  // Close the mobile drawer when a navigation link is tapped.
                  if ((event.target as HTMLElement).closest('a')) {
                    setSidebarOpen(false);
                  }
                }}
              >
                <Brand collapsed={false} />
                <MatterDropdown />
                <Navigation />
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        data-navigation-drawer="true"
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ease-in-out overflow-hidden',
          'bg-[hsl(217,47%,20%)] border-r border-white/10 shadow-[1px_0_0_rgba(255,255,255,0.04)_inset]',
          collapsed ? 'lg:w-20' : 'lg:w-56',
        )}
      >
        <div
          className={cn(
            'flex grow flex-col gap-y-3 overflow-y-auto overflow-x-hidden relative transition-[padding] duration-300',
            collapsed ? 'px-2' : 'px-3',
          )}
        >
          <Brand collapsed={collapsed} />
          {!collapsed && <MatterDropdown />}
          <Navigation collapsed={collapsed} />
        </div>

        {/* Collapse toggle - pinned at bottom, below settings */}
        <div
          className={cn(
            'shrink-0 border-t border-white/10',
            collapsed ? 'px-2 py-2' : 'px-3 py-2',
          )}
        >
          {collapsed ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleCollapsed}
                    className="flex items-center justify-center w-full p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 transition-all duration-200"
                    aria-label={t('expand-sidebar')}
                  >
                    <ChevronRightIcon className="h-[18px] w-[18px]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="text-xs font-medium bg-zinc-900 text-white border-zinc-800 shadow-lg"
                >
                  Expand
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              type="button"
              onClick={toggleCollapsed}
              className="flex items-center gap-2.5 w-full px-2.5 py-[7px] rounded-lg text-[13px] text-zinc-400 hover:text-white hover:bg-white/10 transition-all duration-200"
              aria-label={t('collapse-sidebar')}
            >
              <ChevronLeftIcon className="h-[18px] w-[18px]" />
              <span>Collapse</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Drawer;
